import "@product/rosetta-sdk/custom-piano-styles.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { css, Global } from "@emotion/react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { useDebouncedCallback, useResizeObserver } from "@react-hookz/web";
import { AnimatePresence, domAnimation, LazyMotion } from "framer-motion";
import { Provider as JotaiProvider } from "jotai";
import type { NextApp } from "next";
import { useCallback, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { RelayEnvironmentProvider } from "react-relay";
import { useRelayNextjs } from "relay-nextjs/app";
import { safeJsonParse } from "scmp-sdk";
import { SWRConfig } from "swr";

import { PageTransition } from "shared/components/common/page-transition";
import { config } from "shared/data";
import { useSentrySetup } from "shared/lib/sentry/hooks";
import { globalCss, theme } from "shared/lib/styles";
import { isSsr, notEmpty } from "shared/lib/utils";

import { AdvertisementScripts } from "scmp-app/components/advertisement";
import { AppInitializer } from "scmp-app/components/app-initializer";
import { AppLinkingData } from "scmp-app/components/app-linking-data";
import { TrackingScripts } from "scmp-app/components/tracking";
import { OptimizelyContextProvider } from "scmp-app/lib/optimizely/context";
import type { PageProps } from "scmp-app/lib/relay-nextjs";
import { createClientEnvironmentByPageProps } from "scmp-app/lib/relay-nextjs/helpers";
import { RosettaScripts } from "scmp-app/lib/rosetta/scripts";

import { AfterPageLayoutContainerElementID, FooterElementID } from "./_app/consts";
import {
  DynamicArticlePianoPaywallProvider,
  DynamicC2paProvider,
  DynamicRosettaContextProvider,
  DynamicSdkContextProvider,
  DynamicSnackbarProvider,
} from "./_app/dynamics";
import { AfterPageLayoutContainer, Content, FooterContainer, PageContainer } from "./_app/styles";

const App: NextApp<PageProps> = ({ Component, err, pageProps }) => {
  const { env: environment, ...relayProps } = useRelayNextjs(pageProps, {
    createClientEnvironment: () => createClientEnvironmentByPageProps(pageProps),
  });

  useSentrySetup();

  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  const captureElement = useCallback((element: HTMLDivElement | null) => {
    if (!notEmpty(element)) return;
    setContainerElement(element);
  }, []);

  const handler = useDebouncedCallback(
    (entry: ResizeObserverEntry) => {
      document.body.style.setProperty("--content-container-width", `${entry.contentRect.width}`);
    },
    [],
    100,
  );
  useResizeObserver(containerElement, handler, containerElement !== undefined);

  // For supporting layout per page
  const renderLayout = () => {
    const PageComponent = <Component {...pageProps} {...relayProps} err={err} />;
    if (Component.getLayout) {
      return Component.getLayout(PageComponent);
    }

    return (
      <PageContainer
        $contentResponsivePadding={pageProps.contentConfiguration?.responsivePadding}
        $pageResponsivePadding={pageProps.pageConfiguration?.responsivePadding}
      >
        <AppInitializer pageProps={pageProps}>
          <Content ref={captureElement}>
            <AnimatePresence initial={false} mode="wait">
              <PageTransition animateKey={pageProps.animateKey}>{PageComponent}</PageTransition>
            </AnimatePresence>
          </Content>
        </AppInitializer>
        <FooterContainer id={FooterElementID} />
      </PageContainer>
    );
  };
  return (
    <OptimizelyContextProvider>
      <AppLinkingData />
      <Global
        styles={css`
          ${globalCss}
          ${pageProps.extraGlobalCss}
        `}
      />
      <LazyMotion features={domAnimation}>
        <MuiThemeProvider theme={theme}>
          <JotaiProvider>
            <AdvertisementScripts />
            <TrackingScripts />
            <SWRConfig
              value={{
                onErrorRetry: (_error, _key, _config, revalidate, { retryCount }) => {
                  if (retryCount >= 5) return;
                  setTimeout(() => revalidate({ retryCount }), 5000);
                },
                provider: sessionStorageProvider,
              }}
            >
              <RelayEnvironmentProvider environment={environment}>
                <DynamicArticlePianoPaywallProvider>
                  <DynamicSnackbarProvider>
                    <DynamicSdkContextProvider>
                      <DynamicRosettaContextProvider>
                        <RosettaScripts />
                        <DynamicC2paProvider>
                          <GoogleReCaptchaProvider reCaptchaKey={config.account.recaptchaSiteKey}>
                            {renderLayout()}
                            <AfterPageLayoutContainer id={AfterPageLayoutContainerElementID} />
                          </GoogleReCaptchaProvider>
                        </DynamicC2paProvider>
                      </DynamicRosettaContextProvider>
                    </DynamicSdkContextProvider>
                  </DynamicSnackbarProvider>
                </DynamicArticlePianoPaywallProvider>
              </RelayEnvironmentProvider>
            </SWRConfig>
          </JotaiProvider>
        </MuiThemeProvider>
      </LazyMotion>
    </OptimizelyContextProvider>
  );
};

function sessionStorageProvider(): Map<string, Record<string, unknown>> {
  if (isSsr()) return new Map();
  const map = new Map(
    safeJsonParse<Map<string, Record<string, unknown>>>(
      sessionStorage.getItem("app-cache") ?? "[]",
    ),
  );
  const handler = () => {
    const appCache = JSON.stringify([...map.entries()]);
    sessionStorage.setItem("app-cache", appCache);
  };
  window.addEventListener("pagehide", handler);
  window.addEventListener("beforeunload", handler);
  return map;
}

App.displayName = "App";

export default App;
